import { useCallback, useEffect, useState } from "react";
import { AdminShell } from "./components/AdminShell";
import { AppShell } from "./components/AppShell";
import { useConfirmation } from "./components/ConfirmDialog";
import { LoadingGrid } from "./components/Loading";
import { useAppData } from "./hooks/useAppData";
import { useHashRoute } from "./hooks/useHashRoute";
import { useWorkoutSync } from "./hooks/useWorkoutSync";
import { AdminPage } from "./pages/AdminPage";
import { DashboardPage } from "./pages/DashboardPage";
import { HistoryPage } from "./pages/HistoryPage";
import { LoginPage } from "./pages/LoginPage";
import { NutritionPage } from "./pages/NutritionPage";
import { PlanPage } from "./pages/PlanPage";
import { RecoveryPage } from "./pages/RecoveryPage";
import { SettingsPage } from "./pages/SettingsPage";
import type { TokenResponse } from "./types";
import { completedPlanDates, localDateKey, readReminderSettings, todayPlanDay, writeReminderSettings } from "./utils";

const TOKEN_KEY = "fittracker.token";

export default function App() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const { route, setRoute } = useHashRoute();
  const signOut = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);
  const saveToken = useCallback((next: string) => {
    sessionStorage.setItem(TOKEN_KEY, next);
    setToken(next);
  }, []);

  const data = useAppData({ token, onUnauthorized: signOut });
  const workout = useWorkoutSync({
    token: data.user && !data.user.is_admin ? token : "",
    onUnauthorized: signOut,
    onSessionsChanged: data.refreshSessions,
    onError: data.setError,
  });
  const { confirm, confirmationDialog } = useConfirmation();

  useEffect(() => {
    if (!data.user) return;
    if (data.user.is_admin && route !== "admin") setRoute("admin");
    if (!data.user.is_admin && route === "admin") setRoute("dashboard");
  }, [data.user, route, setRoute]);

  useEffect(() => {
    if (!token || !data.snapshot || data.user?.is_admin) return;
    const check = () => {
      const reminder = readReminderSettings();
      const day = todayPlanDay(data.snapshot);
      if (!reminder.enabled || !day || completedPlanDates(data.sessions).has(day.date)) return;
      const now = new Date();
      const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      if (
        time >= reminder.time
        && reminder.lastDate !== localDateKey(now)
        && "Notification" in window
        && Notification.permission === "granted"
      ) {
        new Notification("FitTracker workout", { body: `${day.title || day.type} is scheduled today.` });
        writeReminderSettings({ ...reminder, lastDate: localDateKey(now) });
      }
    };
    check();
    const timer = window.setInterval(check, 30000);
    return () => window.clearInterval(timer);
  }, [data.sessions, data.snapshot, data.user?.is_admin, token]);

  function authenticated(response: TokenResponse) {
    saveToken(response.access_token);
    setRoute(response.user.is_admin ? "admin" : "dashboard");
  }

  if (!token) return <LoginPage onAuthenticated={authenticated} />;
  if (data.loading && !data.user) return <main className="loading-screen"><div className="logo"><span>FT</span><strong>FitTracker</strong></div><LoadingGrid rows={4} /></main>;
  if (!data.user) return <main className="loading-screen"><p className="error">{data.error || "Unable to load your account."}</p><button className="button secondary" type="button" onClick={signOut}>Return to sign in</button></main>;

  if (data.user.is_admin) {
    return (
      <AdminShell user={data.user} error={data.error} onSignOut={signOut} onDismissError={() => data.setError("")}>
        <AdminPage
          currentUserId={data.user.id}
          stats={data.adminStats}
          users={data.adminUsers}
          settings={data.adminSettings}
          refreshStats={data.loadAdminStats}
          loadUsers={data.loadAdminUsers}
          updateUser={data.updateAdminUser}
          loadSettings={data.loadAdminSettings}
          saveSettings={data.saveAdminSettings}
          onError={data.setError}
        />
      </AdminShell>
    );
  }

  const page = (() => {
    if (route === "plan") return <PlanPage snapshot={data.snapshot} sessions={data.sessions} rebuildPlan={data.rebuildPlan} onError={data.setError} />;
    if (route === "nutrition") return <NutritionPage snapshot={data.snapshot} meals={data.meals} estimateMeal={data.estimateMeal} addMeal={data.addMeal} deleteMeal={data.deleteMeal} clearMeals={data.clearMeals} onEditProfile={() => setRoute("plan")} onError={data.setError} confirmDelete={(kind) => confirm({ title: kind === "all" ? "Clear all meal logs?" : "Delete this meal?", message: kind === "all" ? "This removes every saved meal for this account, not only today." : "Your daily nutrition totals will update immediately.", confirmLabel: kind === "all" ? "Clear meal logs" : "Delete meal", destructive: true })} />;
    if (route === "recovery") return <RecoveryPage snapshot={data.snapshot} wearables={data.wearables} metrics={data.metrics} connectWearable={data.connectWearable} disconnectWearable={data.disconnectWearable} refreshWearables={data.refreshWearables} rebuildPlan={data.rebuildPlan} onCreatePlan={() => setRoute("plan")} onError={data.setError} confirmDisconnect={(provider) => confirm({ title: `Disconnect ${provider}?`, message: "Recovery data from this service will stop updating.", confirmLabel: "Disconnect", destructive: true })} />;
    if (route === "history") return <HistoryPage sessions={data.sessions} />;
    if (route === "settings") return <SettingsPage user={data.user} snapshot={data.snapshot} updateName={data.updateName} changePassword={data.changePassword} revokeSessions={data.revokeSessions} deleteAccount={data.deleteAccount} onToken={saveToken} onDeleted={signOut} onEditProfile={() => setRoute("plan")} onError={data.setError} confirmRevoke={() => confirm({ title: "Sign out other sessions?", message: "Every existing token will be revoked. This browser will receive a replacement token.", confirmLabel: "Revoke sessions", destructive: true })} />;
    return <DashboardPage user={data.user} snapshot={data.snapshot} sessions={data.sessions} wearables={data.wearables} metrics={data.metrics} workout={workout} onCreatePlan={() => setRoute("plan")} onManageRecovery={() => setRoute("recovery")} onConfirmCancel={() => confirm({ title: "Discard this workout?", message: "The workout will end on every connected device.", confirmLabel: "Discard workout", destructive: true })} />;
  })();

  return (
    <>
      <AppShell user={data.user} route={route} connected={workout.connected} error={data.error} onRoute={setRoute} onSignOut={signOut} onDismissError={() => data.setError("")}>
        {page}
      </AppShell>
      {confirmationDialog}
    </>
  );
}
